import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Circle, MinusCircle, TriangleAlert } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/utils';
import { type ChecklistStep, type StepState } from '../hooks/use-checklist-progress';
import { useChecklistStore } from '../store/checklist-store';

interface ChecklistStepCardProps {
  step: ChecklistStep;
  stepNumber: number;
}

const ICON: Record<StepState, typeof Circle> = {
  pending: Circle,
  active: Circle,
  done: CheckCircle2,
  skipped: MinusCircle,
  blocked: TriangleAlert,
};

export function ChecklistStepCard({ step, stepNumber }: ChecklistStepCardProps) {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const skipStep = useChecklistStore((s) => s.skip);
  const unskipStep = useChecklistStore((s) => s.unskip);

  const Icon = ICON[step.state];
  const isExpanded = step.state === 'active';
  const showActions = step.state === 'active' || step.state === 'pending';

  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2.5 transition-colors',
        step.state === 'active' &&
          'border-amber-500/30 bg-amber-500/[0.04] ring-2 ring-amber-500/[0.08]',
        step.state === 'done' && 'border-border bg-white/[0.02]',
        step.state === 'pending' && 'border-border bg-white/[0.02]',
        step.state === 'skipped' && 'border-border/40 bg-white/[0.015]',
      )}
      aria-current={step.state === 'active' ? 'step' : undefined}
    >
      <div className="flex items-start gap-2.5">
        <span
          className={cn(
            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center',
            step.state === 'active' && 'text-amber-500',
            step.state === 'done' && 'text-emerald-500',
            step.state === 'pending' && 'text-muted-foreground',
            step.state === 'skipped' && 'text-muted-foreground/60',
          )}
          aria-hidden
        >
          {step.state === 'active' ? (
            <span className="relative flex h-3 w-3 items-center justify-center">
              <span className="absolute inline-flex h-3 w-3 animate-ping rounded-full bg-amber-500/40" />
              <Circle className="relative h-3 w-3 fill-amber-500 text-amber-500" />
            </span>
          ) : (
            <Icon className="h-4 w-4" />
          )}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-[13px] font-medium',
                step.state === 'skipped' ? 'text-muted-foreground' : 'text-foreground',
              )}
            >
              {t(`checklist.steps.${step.id}.title`)}
            </span>
            {step.hardDep && (
              <span className="rounded-full bg-amber-500/14 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-amber-500">
                {t('checklist.required')}
              </span>
            )}
          </div>
          {isExpanded && (
            <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
              {t(`checklist.steps.${step.id}.subtitle`)}
            </p>
          )}
          {step.state === 'skipped' && (
            <button
              type="button"
              onClick={() => unskipStep(step.id)}
              className="mt-1 text-[11px] text-amber-500 hover:underline"
            >
              {t('checklist.undoSkip')} →
            </button>
          )}
          {showActions && (
            <div className="mt-2.5 flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => navigate(step.cta)}
                className={cn(
                  'h-7 px-2.5 text-xs',
                  step.state === 'active'
                    ? 'bg-amber-500 text-[#0a0b10] hover:bg-amber-400'
                    : 'bg-white/[0.04] text-amber-500 hover:bg-white/[0.08]',
                )}
              >
                {t(`checklist.steps.${step.id}.cta`)}
                <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
              {!step.hardDep && (
                <button
                  type="button"
                  onClick={() => skipStep(step.id)}
                  className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
                >
                  {t('checklist.skip')}
                </button>
              )}
            </div>
          )}
        </div>
        <span className="font-mono text-[10px] text-muted-foreground/50">{stepNumber}</span>
      </div>
    </div>
  );
}
